export const degreesToRadians = (deg) => (deg * Math.PI) / 180;

export const segmentBandQuad = (a, b, halfWidth, extendPx) => {
    if (!a || !b) {
        return null;
    }

    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy);
    if (!Number.isFinite(len) || len < 1e-6) {
        return null;
    }

    const ux = dx / len;
    const uy = dy / len;
    const nx = -uy;
    const ny = ux;

    const ex = ux * (extendPx || 0);
    const ey = uy * (extendPx || 0);

    const ax = a[0] - ex;
    const ay = a[1] - ey;
    const bx = b[0] + ex;
    const by = b[1] + ey;

    const wx = nx * halfWidth;
    const wy = ny * halfWidth;

    return [
        [ax + wx, ay + wy],
        [bx + wx, by + wy],
        [bx - wx, by - wy],
        [ax - wx, ay - wy]
    ];
};

export const longestEdgeAngleRad = (coordinates) => {
    const outerRing = coordinates?.[0];
    if (!outerRing || outerRing.length < 2) {
        return 0;
    }

    let bestLen2 = 0;
    let bestAngle = 0;

    for (let i = 1; i < outerRing.length; i += 1) {
        const a = outerRing[i - 1];
        const b = outerRing[i];
        if (!a || !b) {
            continue;
        }
        const dx = b[0] - a[0];
        const dy = b[1] - a[1];
        const len2 = dx * dx + dy * dy;
        if (len2 > bestLen2) {
            bestLen2 = len2;
            bestAngle = Math.atan2(dy, dx);
        }
    }

    return bestAngle;
};

export const insetSegmentEnds = (a, b, endBufferPx) => {
    if (!a || !b) {
        return null;
    }

    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy);
    if (!Number.isFinite(len) || len < 1e-6) {
        return null;
    }

    const endBuffer = Math.max(0, endBufferPx || 0);
    if (endBuffer > 0 && len <= endBuffer * 2 + 1e-6) {
        return null;
    }

    const ux = dx / len;
    const uy = dy / len;
    const inset = endBuffer > 0 ? Math.min(endBuffer, (len / 2) - 1e-6) : 0;

    const a2 = inset ? [a[0] + ux * inset, a[1] + uy * inset] : a;
    const b2 = inset ? [b[0] - ux * inset, b[1] - uy * inset] : b;

    return {
        a2,
        b2,
        dx,
        dy,
        len,
        ux,
        uy,
        inset
    };
};

export const ringsExtentPx = (coordinates) => {
    if (!coordinates || coordinates.length === 0) {
        return null;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const ring of coordinates) {
        if (!ring || ring.length === 0) {
            continue;
        }
        for (const p of ring) {
            if (!p) {
                continue;
            }
            const x = p[0];
            const y = p[1];
            if (!Number.isFinite(x) || !Number.isFinite(y)) {
                continue;
            }
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
        }
    }

    if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
        return null;
    }

    return { minX, minY, maxX, maxY };
};
